<template>
  <div class="login-container">
    <div v-if="showSplash" class="splash-screen">
      <img src="/earworm.png" alt="Hop To It Logo" class="splash-logo" />
    </div>
    <div v-else class="login-form">
      <h1>Login</h1>
      <form @submit.prevent="handleLogin">
        <div class="form-group">
          <input 
            id="username" 
            v-model="username" 
            class="auth-input" 
            type="text" 
            placeholder="Username"
            autocomplete="username"
          />
        </div>
        <div class="form-group">
          <input 
            id="password" 
            v-model="password" 
            class="auth-input" 
            type="password" 
            placeholder="Password"
            autocomplete="current-password"
          />
        </div>
        <button type="submit" :disabled="loading">Login</button>
        <button type="button" @click="handlePasskey" :disabled="loading" style="margin-top: 1rem;">Login with Passkey</button>
        <button type="button" @click="goRegister" style="margin-top: 1rem;">Register</button>
        <div v-if="error" class="error">{{ error }}</div>
      </form>
      <!-- TTS (temp) controls -->
      <div class="form-group">
        <textarea
          id="ttsText"
          v-model="ttsText"
          class="auth-input auth-textarea"
          rows="3"
          placeholder="Temp: text to speak"
        ></textarea>
      </div>
      <div class="form-group">
        <textarea
          id="ttsStyle"
          v-model="ttsStyle"
          class="auth-input auth-textarea"
          rows="3"
          placeholder="Temp: style / instructions"
        ></textarea>
      </div>
      <button type="button" @click="handleTts" :disabled="loading || ttsLoading" style="margin-top: 1rem;">Speak (temp)</button>
      <div v-if="ttsError" class="error">{{ ttsError }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { API_BASE_URL } from '../services/database';
// Add this log to verify the API base URL at runtime
console.log('API_BASE_URL:', API_BASE_URL);
const router = useRouter();
const username = ref('');
const password = ref('');
const error = ref('');
const loading = ref(false);
const showSplash = ref(true);

const ttsText = ref('');
const ttsStyle = ref('');
const ttsError = ref('');
const ttsLoading = ref(false);
const ttsAudioUrl = ref<string | null>(null);

onMounted(() => {
  setTimeout(() => {
    showSplash.value = false;
  }, 1200);
});

const handleTts = async () => {
  ttsLoading.value = true;
  ttsError.value = '';
  try {
    if (!ttsText.value.trim()) {
      ttsError.value = 'Enter some text to speak.';
      return;
    }

    const res = await fetch(`${API_BASE_URL}/api/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: ttsText.value,
        instructions: ttsStyle.value,
        // ElevenLabs: use voice_id (server can also default via ELEVENLABS_TTS_VOICE_ID)
        voice_id: 'wM4tem0x77Xpku8Wvh3u',
        format: 'mp3'
      })
    });

    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || 'TTS failed');
    }

    const buf = await res.arrayBuffer();
    const blob = new Blob([buf], { type: res.headers.get('content-type') || 'audio/mpeg' });

    if (ttsAudioUrl.value) URL.revokeObjectURL(ttsAudioUrl.value);
    ttsAudioUrl.value = URL.createObjectURL(blob);

    const audio = new Audio(ttsAudioUrl.value);
    await audio.play();
  } catch (err) {
    ttsError.value = err instanceof Error ? err.message : 'TTS failed';
  } finally {
    ttsLoading.value = false;
  }
};

const handleLogin = async () => {
  loading.value = true;
  error.value = '';
  try {
    const uname = username.value.trim();
    const pwd = password.value;
    if (!uname || !pwd) {
      throw new Error('Username and password required');
    }

    const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: uname, password: pwd })
    });
    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || 'Login failed');
    }
    const user = await res.json();
    console.log('Login response:', user);
    console.log('Session token in response:', user.sessionToken ? `${user.sessionToken.substring(0, 8)}...` : 'NOT FOUND');
    
    // Store all session data in sessionStorage (no cookies needed)
    const sessionData = {
      ...user,
      sessionId: user.sessionToken,
      userId: user.userId || user.id
    };
    sessionStorage.setItem('currentUser', JSON.stringify(sessionData));
    console.log('Stored in sessionStorage:', JSON.parse(sessionStorage.getItem('currentUser') || '{}'));
    
    // No delay needed since we're not using cookies
    console.log('Login successful, session stored in sessionStorage');
    
    router.push('/dashboard');
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Login failed';
  } finally {
    loading.value = false;
  }
};

const goRegister = () => {
  router.push({ name: 'register' });
};

const handlePasskey = async () => {
  loading.value = true;
  error.value = '';
  try {
    // Step 1: Request challenge from backend (no email)
    const challengeRes = await fetch(`${API_BASE_URL}/api/auth/webauthn/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    if (!challengeRes.ok) throw new Error('Failed to get passkey challenge');
    const challengeData = await challengeRes.json();

    // Step 2: Use WebAuthn API to get assertion
    const publicKey = challengeData.publicKeyCredentialRequestOptions;
    // Convert challenge from array to Uint8Array
    publicKey.challenge = new Uint8Array(publicKey.challenge);
    // Convert allowCredentials IDs from arrays to Uint8Array
    publicKey.allowCredentials = publicKey.allowCredentials?.map((cred: any) => ({
      ...cred,
      id: new Uint8Array(cred.id)
    }));

    const assertion = await navigator.credentials.get({ publicKey });
    if (!assertion) throw new Error('Passkey authentication failed');
    const cred = assertion as PublicKeyCredential;
    const resp = cred.response as AuthenticatorAssertionResponse;

    // Step 3: Send assertion to backend for verification
    const credential = {
      id: cred.id,
      rawId: btoa(String.fromCharCode(...new Uint8Array(cred.rawId))),
      type: cred.type,
      response: {
        authenticatorData: btoa(String.fromCharCode(...new Uint8Array(resp.authenticatorData))),
        clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(resp.clientDataJSON))),
        signature: btoa(String.fromCharCode(...new Uint8Array(resp.signature))),
        userHandle: resp.userHandle ? btoa(String.fromCharCode(...new Uint8Array(resp.userHandle))) : null
      }
    };

    const verifyRes = await fetch(`${API_BASE_URL}/api/auth/webauthn/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential })
    });
    if (!verifyRes.ok) throw new Error('Passkey verification failed');
    const user = await verifyRes.json();
    sessionStorage.setItem('currentUser', JSON.stringify(user));
    router.push('/dashboard');
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Passkey login failed';
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
/* Splash screen styles */
.splash-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  background: #f5f5f5;
}
.splash-logo {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}
.login-form {
  background: white;
  padding: 2rem;
  border-radius: 4px;
  border: 1px solid #ddd;
  width: 100%;
  max-width: 400px;
}
h1 {
  text-align: center;
  margin-bottom: 2rem;
  color: #333;
}
.auth-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}
.auth-input:focus {
  outline: none;
  border-color: #666;
}
.auth-textarea {
  resize: vertical;
  min-height: 72px;
}
button {
  width: 100%;
  padding: 0.75rem;
  background-color: #666;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
}
button:hover:not(:disabled) {
  background-color: #555;
}
button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
.error {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #f8f8f8;
  border: 1px solid #ddd;
  border-radius: 4px;
  color: #333;
}
</style>